import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAdminAuth } from "@/hooks/use-admin";

// Pages
import Login from "@/pages/Login";
import VerifyOTP from "@/pages/VerifyOTP";
import Dashboard from "@/pages/Dashboard";
import Newsletter from "@/pages/Newsletter";
import Content from "@/pages/Content";
import Users from "@/pages/Users";
import Analytics from "@/pages/Analytics";
import Settings from "@/pages/Settings";

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isAdmin, isLoading } = useAdminAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-semibold">Access Denied</h1>
          <p className="text-muted-foreground">
            You don't have permission to access the admin panel.
          </p>
          <button
            onClick={() => window.location.href = "/login"}
            className="text-primary hover:underline"
          >
            Sign in with a different account
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

// Guest route wrapper (only for login pages)
function GuestRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isAdmin, isLoading } = useAdminAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated && isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <TooltipProvider>
      <Toaster
        position="top-right"
        toastOptions={{
          className: "glass",
          duration: 4000,
        }}
      />
      <Routes>
        {/* Auth routes */}
        <Route
          path="/login"
          element={
            <GuestRoute>
              <Login />
            </GuestRoute>
          }
        />
        {/* Verify OTP - NOT wrapped in GuestRoute to prevent redirect race condition */}
        <Route
          path="/verify-otp"
          element={<VerifyOTP />}
        />

        {/* Protected admin routes */}
        <Route
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="newsletter/*" element={<Newsletter />} />
          <Route path="content/*" element={<Content />} />
          <Route path="users/*" element={<Users />} />
          <Route path="analytics/*" element={<Analytics />} />
          <Route path="settings/*" element={<Settings />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </TooltipProvider>
  );
}
